/**
 * ExistingLiabilitiesPanel — Self-service CRUD for "Pasivos Existentes" (pre-project debt).
 *
 * Reusable module — no hard-coding. Each developer captures their dragged debt
 * with structured params: principal, rate (fixed | floating TIIE+spread), repayment scheme,
 * priority, interest treatment.
 *
 * Engine accounting contract (enforced by /api/analyzer/run + self_check):
 *   - The liability principal is a return of pre-existing capital to the lender,
 *     NOT a new project cost. Only INTEREST reduces equity return.
 *   - NPV(with) ≈ NPV(without) − PV(interest) with timing residual < $50K.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, AlertCircle, Edit3, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useConfirm } from '../../components/ui/confirm-dialog';
import {
  RATE_MODES,
  INTEREST_TREATMENTS,
  LIABILITY_PRIORITIES,
  LIABILITY_REPAYMENT_OPTIONS,
  REPAYMENT_MODES,
  buildBulletSchedule,
} from '../../lib/financing-taxonomy';
import { BACKEND_URL } from '../../lib/api';

const API = BACKEND_URL;

const RATE_LABELS = RATE_MODES;
const PRIORITY_LABELS = LIABILITY_PRIORITIES;
const TREATMENT_LABELS = INTEREST_TREATMENTS;

const EMPTY_LIABILITY = {
  label: '',
  // Sprint 3 · v2.8.4 · Taxonomía formal
  liability_type: 'dragged',
  principal_balance: 0,
  start_month: 1,
  rate_mode: 'floating',
  fixed_rate_annual: 0,
  tiie_base: 0.0675,
  tiie_spread_bps: 450,
  repayment_mode: 'liberation_per_unit',
  liberation_per_unit: 0,
  manual_schedule_json: '',
  fixed_monthly_amount: 0,
  pct_of_sales: 0,
  priority: 'senior_to_equity',
  interest_treatment: 'served_monthly',
  notes: '',
};

const LIABILITY_TYPES = {
  dragged: { label: 'Pasivo arrastrado', short: 'Arrastrado', color: '#0369A1', bg: 'rgba(3,105,161,0.08)', desc: 'Deuda pre-proyecto (ej. crédito puente vigente antes de arrancar la nueva etapa).' },
  structured: { label: 'Deuda estructurada', short: 'Estructurada', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', desc: 'Mezzanine / socios acreedores / deuda subordinada del waterfall.' },
};

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPct(v) {
  return `${(Number(v) * 100).toFixed(2)}%`;
}

function authHeaders() {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function ExistingLiabilitiesPanel({ scenarioId, legacyExistingBalance = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);  // S2 · bloquea doble-click client-side
  const confirm = useConfirm();

  const load = async () => {
    if (!scenarioId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/scenarios/${scenarioId}/liabilities`, { headers: authHeaders() });
      setItems(data.liabilities || []);
    } catch (e) {
      toast.error('Error cargando pasivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [scenarioId]);

  const totalPrincipal = useMemo(() => items.reduce((s, x) => s + (x.principal_balance || 0), 0), [items]);

  const startCreate = () => {
    if (legacyExistingBalance > 0) {
      toast.error('Este escenario tiene un saldo legacy (existing_loan_balance) sin migrar. Pídele al admin que ejecute el script S4 antes de añadir pasivos estructurados (riesgo de doble conteo).');
      return;
    }
    setDraft({ ...EMPTY_LIABILITY });
    setCreating(true);
    setEditingId(null);
  };

  const startEdit = (item) => {
    setDraft({ ...EMPTY_LIABILITY, ...item });
    setEditingId(item.id);
    setCreating(false);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditingId(null);
    setCreating(false);
  };

  const saveDraft = async () => {
    let payload = {
      ...draft,
      principal_balance: parseFloat(draft.principal_balance) || 0,
      start_month: parseInt(draft.start_month) || 1,
      fixed_rate_annual: parseFloat(draft.fixed_rate_annual) || 0,
      tiie_base: parseFloat(draft.tiie_base) || 0,
      tiie_spread_bps: parseFloat(draft.tiie_spread_bps) || 0,
      liberation_per_unit: parseFloat(draft.liberation_per_unit) || 0,
      fixed_monthly_amount: parseFloat(draft.fixed_monthly_amount) || 0,
      pct_of_sales: parseFloat(draft.pct_of_sales) || 0,
    };
    if (!payload.label) {
      toast.error('La etiqueta es obligatoria');
      return;
    }
    if (payload.principal_balance <= 0) {
      toast.error('El principal debe ser mayor a cero');
      return;
    }
    // Bullet emulado → se persiste como manual_schedule con un solo pago al vencimiento.
    // El motor v2.5.0 no implementa bullet explícito; usamos manual_schedule como helper UI.
    if (payload.repayment_mode === 'bullet_emulated') {
      const term = parseInt(draft.bullet_term_months) || 12;
      payload.manual_schedule_json = buildBulletSchedule(payload.principal_balance, payload.start_month, term);
      payload.repayment_mode = 'manual_schedule';
      payload.notes = (payload.notes || '') + ` [bullet emulado: pago único de $${payload.principal_balance.toLocaleString('en-US')} al mes ${payload.start_month + term - 1}]`.trim();
    }
    // S2 · Idempotencia: el cliente genera un UUIDv4 fresco POR CADA submit.
    // Doble-click → mismo request en vuelo → mismo key → el backend devuelve el mismo recurso.
    // Si el usuario quiere agregar la "misma deuda" otra vez (intención legítima), debe abrir
    // un nuevo form (con cancelEdit/startCreate) → genera key distinta → backend crea nuevo doc.
    if (creating) {
      payload.idempotency_key = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
    }
    if (creating && submitting) {
      // Bloqueo client-side complementario: ignora doble-click mientras el primer POST sigue en vuelo.
      return;
    }
    setSubmitting(true);
    try {
      if (creating) {
        await axios.post(`${API}/api/scenarios/${scenarioId}/liabilities`, payload, { headers: authHeaders() });
        toast.success('Pasivo creado');
      } else {
        await axios.put(`${API}/api/scenarios/${scenarioId}/liabilities/${editingId}`, payload, { headers: authHeaders() });
        toast.success('Pasivo actualizado');
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSubmitting(false);
    }
  };

  const removeItem = async (id) => {
    const ok = await confirm({
      title: '¿Eliminar pasivo?',
      description: 'El pasivo se removerá del escenario y de las corridas futuras.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/scenarios/${scenarioId}/liabilities/${id}`, { headers: authHeaders() });
      toast.success('Pasivo eliminado');
      await load();
    } catch (e) {
      console.error('[Liabilities] delete failed', e);
      toast.error('Error eliminando');
    }
  };

  return (
    <div className="space-y-4" data-testid="existing-liabilities-panel">
      {legacyExistingBalance > 0 && (
        <div
          className="rounded-2xl border p-4 flex items-start gap-3"
          style={{ background: '#FEF3C7', borderColor: '#F59E0B' }}
          data-testid="legacy-liability-banner"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: '#B45309' }} />
          <div className="text-xs leading-relaxed" style={{ color: '#78350F' }}>
            <strong>Aviso: escenario con campo heredado.</strong> Este escenario aún declara{' '}
            <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3 }}>
              existing_loan_balance = ${(legacyExistingBalance).toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </code>
            . Debe migrarse a la colección estructurada antes de añadir pasivos aquí (riesgo de doble conteo en{' '}
            <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3 }}>Check 6</code>
            ). El admin debe ejecutar:{' '}
            <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 3 }}>
              python scripts/s4_migrate_legacy_liabilities.py --commit
            </code>
            .
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
              Pasivos existentes · taxonomía formal (Sprint 3)
            </h5>
            <p className="text-xs mt-1" style={{ color: '#64748B' }}>
              Separa <strong style={{ color: LIABILITY_TYPES.dragged.color }}>pasivos arrastrados</strong> (deuda pre-proyecto)
              de <strong style={{ color: LIABILITY_TYPES.structured.color }}>deuda estructurada</strong>
              (mezzanine / socios acreedores). Ambos se procesan por el mismo motor de repago; el tipo
              agrupa el reporte por acreedor y el Estado de Caja consolidado.
            </p>
          </div>
          <Button onClick={startCreate} size="sm" data-testid="add-liability-btn" style={{ background: 'var(--financial-primary)', color: 'white' }}>
            <Plus className="w-4 h-4 mr-1" /> Agregar pasivo
          </Button>
        </div>

        {loading && <p className="text-xs" style={{ color: '#94A3B8' }}>Cargando...</p>}

        {!loading && items.length === 0 && !creating && (
          <div className="flex items-center gap-2 py-6 px-4 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
            <AlertCircle className="w-4 h-4" style={{ color: '#94A3B8' }} />
            <p className="text-xs" style={{ color: '#64748B' }}>
              No hay pasivos preexistentes capturados. Agrega uno si el proyecto arranca con deuda heredada.
            </p>
          </div>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Etiqueta</th>
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Tipo</th>
                  <th className="text-right py-2 px-2" style={{ color: '#94A3B8' }}>Principal</th>
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Tasa</th>
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Repago</th>
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Trato interés</th>
                  <th className="text-left py-2 px-2" style={{ color: '#94A3B8' }}>Prioridad</th>
                  <th className="text-center py-2 px-2" style={{ color: '#94A3B8' }}>Mes inicio</th>
                  <th className="text-right py-2 px-2" style={{ color: '#94A3B8' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const t = LIABILITY_TYPES[it.liability_type || 'dragged'];
                  return (
                    <tr key={it.id} className="border-b border-border/20 hover:bg-muted/30" data-testid={`liability-row-${it.id}`}>
                      <td className="py-2 px-2 font-medium" style={{ color: '#1E293B' }}>{it.label}</td>
                      <td className="py-2 px-2">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                          style={{ background: t.bg, color: t.color }}
                          data-testid={`liab-type-badge-${it.id}`}
                        >
                          {t.short}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: '#1E293B' }}>{fmtMoney(it.principal_balance)}</td>
                      <td className="py-2 px-2" style={{ color: '#475569' }}>
                        {it.rate_mode === 'fixed' ? fmtPct(it.fixed_rate_annual) : `TIIE ${fmtPct(it.tiie_base)} + ${it.tiie_spread_bps} bps`}
                      </td>
                      <td className="py-2 px-2" style={{ color: '#475569' }}>{REPAYMENT_MODES[it.repayment_mode] || it.repayment_mode}</td>
                      <td className="py-2 px-2" style={{ color: '#475569' }}>{TREATMENT_LABELS[it.interest_treatment] || it.interest_treatment}</td>
                      <td className="py-2 px-2" style={{ color: '#475569' }}>{PRIORITY_LABELS[it.priority] || it.priority}</td>
                      <td className="py-2 px-2 text-center" style={{ color: '#475569' }}>M{it.start_month}</td>
                      <td className="py-2 px-2 text-right">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`edit-liability-${it.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeItem(it.id)} data-testid={`delete-liability-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="font-semibold">
                  <td colSpan={2} className="py-2 px-2" style={{ color: '#1E293B' }}>Total principal</td>
                  <td className="py-2 px-2 text-right" style={{ color: '#1E293B' }} data-testid="liabilities-total-principal">{fmtMoney(totalPrincipal)}</td>
                  <td colSpan={6}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {draft && <LiabilityForm draft={draft} setDraft={setDraft} onSave={saveDraft} onCancel={cancelEdit} isCreating={creating} submitting={submitting} />}
      </div>
    </div>
  );
}

function LiabilityForm({ draft, setDraft, onSave, onCancel, isCreating, submitting }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  return (
    <div className="mt-4 p-4 rounded-xl border border-dashed" style={{ borderColor: 'var(--financial-primary)', background: 'var(--bg-elevated)' }} data-testid="liability-form">
      <h6 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--financial-primary)' }}>
        {isCreating ? 'Nuevo pasivo' : 'Editar pasivo'}
      </h6>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Etiqueta" required>
          <Input value={draft.label} onChange={e => set('label', e.target.value)} placeholder="p.ej. Crédito Banorte Etapa 1" data-testid="liab-label" />
        </Field>
        <Field label="Tipo (taxonomía Sprint 3)">
          <Select value={draft.liability_type || 'dragged'} onValueChange={v => set('liability_type', v)}>
            <SelectTrigger data-testid="liab-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(LIABILITY_TYPES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] mt-1" style={{ color: '#64748B' }}>
            {LIABILITY_TYPES[draft.liability_type || 'dragged']?.desc || ''}
          </div>
        </Field>
        <Field label="Saldo principal ($)" required>
          <Input type="number" value={draft.principal_balance} onChange={e => set('principal_balance', e.target.value)} data-testid="liab-principal" />
        </Field>
        <Field label="Mes inicio">
          <Input type="number" value={draft.start_month} min={1} onChange={e => set('start_month', e.target.value)} data-testid="liab-start-month" />
        </Field>

        <Field label="Modo de tasa">
          <Select value={draft.rate_mode} onValueChange={v => set('rate_mode', v)}>
            <SelectTrigger data-testid="liab-rate-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RATE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        {draft.rate_mode === 'fixed' && (
          <Field label="Tasa fija anual (decimal, ej. 0.14)">
            <Input type="number" step="0.001" value={draft.fixed_rate_annual} onChange={e => set('fixed_rate_annual', e.target.value)} data-testid="liab-fixed-rate" />
          </Field>
        )}
        {draft.rate_mode === 'floating' && (
          <>
            <Field label="TIIE base (decimal, ej. 0.0675)">
              <Input type="number" step="0.001" value={draft.tiie_base} onChange={e => set('tiie_base', e.target.value)} data-testid="liab-tiie-base" />
            </Field>
            <Field label="Spread (bps, ej. 850)">
              <Input type="number" value={draft.tiie_spread_bps} onChange={e => set('tiie_spread_bps', e.target.value)} data-testid="liab-tiie-spread" />
            </Field>
          </>
        )}

        <Field label="Esquema de repago">
          <Select value={draft.repayment_mode} onValueChange={v => set('repayment_mode', v)}>
            <SelectTrigger data-testid="liab-repay-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LIABILITY_REPAYMENT_OPTIONS.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        {draft.repayment_mode === 'liberation_per_unit' && (
          <Field label="Liberación por unidad ($/unidad entregada)">
            <Input type="number" value={draft.liberation_per_unit} onChange={e => set('liberation_per_unit', e.target.value)} data-testid="liab-liberation" />
          </Field>
        )}
        {draft.repayment_mode === 'fixed_monthly' && (
          <Field label="Monto mensual ($)">
            <Input type="number" value={draft.fixed_monthly_amount} onChange={e => set('fixed_monthly_amount', e.target.value)} data-testid="liab-fixed-monthly" />
          </Field>
        )}
        {draft.repayment_mode === 'pct_of_sales' && (
          <Field label="% del flujo de ventas (decimal, 0.10 = 10%)">
            <Input type="number" step="0.001" value={draft.pct_of_sales} onChange={e => set('pct_of_sales', e.target.value)} data-testid="liab-pct-sales" />
          </Field>
        )}
        {draft.repayment_mode === 'manual_schedule' && (
          <Field label="Tabla manual JSON" className="md:col-span-3">
            <textarea
              value={draft.manual_schedule_json}
              onChange={e => set('manual_schedule_json', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-xs rounded-lg border border-border/40 font-mono"
              placeholder='[{"month": 6, "amount": 500000}, {"month": 12, "amount": 1000000}]'
              data-testid="liab-manual-schedule"
            />
          </Field>
        )}
        {draft.repayment_mode === 'bullet_emulated' && (
          <Field label="Plazo del bullet (meses)" className="md:col-span-3">
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                value={draft.bullet_term_months || 12}
                onChange={e => set('bullet_term_months', e.target.value)}
                style={{ maxWidth: 160 }}
                data-testid="liab-bullet-term"
              />
              <span className="text-[10px]" style={{ color: '#64748B' }}>
                Pago único = ${(Number(draft.principal_balance) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                {' '}en mes {(Number(draft.start_month) || 1) + (Number(draft.bullet_term_months) || 12) - 1}.
                Se emula como <code>manual_schedule</code> de un solo pago al vencimiento (el motor v2.5.0 no implementa bullet explícito).
              </span>
            </div>
          </Field>
        )}

        <Field label="Prioridad">
          <Select value={draft.priority} onValueChange={v => set('priority', v)}>
            <SelectTrigger data-testid="liab-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Tratamiento del interés">
          <Select value={draft.interest_treatment} onValueChange={v => set('interest_treatment', v)}>
            <SelectTrigger data-testid="liab-interest-treatment"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TREATMENT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Notas" className="md:col-span-3">
          <Input value={draft.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Contexto adicional" data-testid="liab-notes" />
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={onCancel} data-testid="liab-cancel-btn">
          <X className="w-4 h-4 mr-1" /> Cancelar
        </Button>
        <Button size="sm" onClick={onSave} disabled={!!submitting} data-testid="liab-save-btn" style={{ background: 'var(--financial-primary)', color: 'white', opacity: submitting ? 0.6 : 1 }}>
          <Save className="w-4 h-4 mr-1" /> {submitting ? 'Guardando…' : (isCreating ? 'Crear' : 'Guardar cambios')}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, required, className = '', children }) {
  return (
    <div className={className}>
      <Label className="text-xs font-medium mb-1 block" style={{ color: '#475569' }}>
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
      </Label>
      {children}
    </div>
  );
}

export default ExistingLiabilitiesPanel;
